import { ActionTree } from 'vuex';
import RootState from '@/store/root_state';
import { UndergradDisciplineState } from '../disciplines-instances/state';
import {
  Article,
  Exercise,
  ExerciseType,
  LessonItem,
  UndergradLesson,
} from '@/models/undergrad/undegrad-lesson.model';
import { LessonReaderService } from '@/services/undergrad/lessons-reader.service';
import { UserModel } from '@descomplica/dex-models';
import {
  ContentVideo,
  ContentText,
  ContentImage,
  Content,
  Alternative,
} from '@/models/undergrad/undergrad-content.model';
import {
  AlternativeType,
  CommonContentType,
} from '@/models/undergrad/services/lessons-reader.model';
import { UnifiedCommentModel } from '@/models/UnifiedCommentModel';
import { Solution } from '@/models/undergrad/undergrad-solution.model';
import { normalize } from 'normalizr';
import { undergradLessons } from '../undergrad_api_normalizr_schema';
import SentryClient from '@/helpers/sentry';
import { UserGradesService } from '@/services/undergrad/pegasus-pepe-legal/user-grades.service';
import { ErrorHandler } from '@/helpers/errorHandler';
import i18n from '@/i18n';
import { ExercisesEvaluationService } from '@/services/undergrad/pegasus-pepe-legal/exercises-evaluation.service';
import { normalizeReply } from './helper';

interface IPayload {
  lesson: UndergradLesson;
  user: UserModel;
}

interface IAddLessonReplyPayload {
  lessonId: string;
  exerciseId: string;
  dexExerciseId: number;
  userId: number;
  alternativeId: number;
  answer: number;
}

const mapContent = (content: CommonContentType): Content | null => {
  if (content.video) {
    const video = new ContentVideo();
    video.providerId = content.video.providerId;
    video.providerType = content.video.providerType;

    return video;
  }

  if (content.text) {
    const text = new ContentText();
    text.body = content.text;

    return text;
  }

  if (content.image) {
    const image = new ContentImage();
    image.description = content.image.description;
    image.resizeFactor = content.image.resizeFactor;
    image.source = content.image.source;
    image.url = content.image.url;

    return image;
  }

  return null;
};

const mapContents = (contents: CommonContentType[]) => {
  const itemContents: Content[] = [];

  contents.forEach((content) => {
    const mappedContent = mapContent(content);

    if (mappedContent) {
      itemContents.push(mappedContent);
    }
  });

  return itemContents;
};

const mapAlternatives = (alternatives: AlternativeType[]): Alternative[] => {
  return alternatives.map((al) => {
    const alternative = new Alternative();
    alternative.id = al.id;
    alternative.correct = al.correct;
    alternative.position = al.position;
    alternative.contents = mapContents(al.contents);

    return alternative;
  });
};

const actions: ActionTree<UndergradDisciplineState, RootState> = {
  async PULL_LESSON({ commit }, { slug }: { slug: string }) {
    const lesson = await LessonReaderService.getLessonBySlug(slug);
    const normalizedLessons = normalize(lesson, undergradLessons);

    commit('SET_LESSONS', normalizedLessons.entities);
  },
  async PULL_LESSON_CONTENTS({ commit }, payload: IPayload) {
    const { lesson, user } = payload;

    try {
      const promises = lesson.items?.map(async (item: LessonItem) => {
        if (item instanceof Article) {
          const contents = await LessonReaderService.getArticleInfoFromDex(item.dexArticleId);

          item.contents = mapContents(contents);
        }

        if (item instanceof Exercise) {
          if (item.type === ExerciseType.DISCURSIVE || item.type === ExerciseType.PRACTICE) {
            const { comments, contents } =
              await LessonReaderService.getDiscursiveExerciseInfoFromDex(
                item.dexExerciseId,
                user.id,
              );

            item.contents = mapContents(contents);
            item.comments = comments.map((comment) => new UnifiedCommentModel(comment, user));
          }

          if (item.type === ExerciseType.OBJECTIVE || item.type === ExerciseType.OBJECTIVE_TRIVIA) {
            const { comments, contents, alternatives, replies, solutions } =
              await LessonReaderService.getObjectiveExerciseInfoFromDex(
                item.dexExerciseId,
                user.id,
              );

            item.replies = replies;
            item.contents = mapContents(contents);
            item.alternatives = mapAlternatives(alternatives);
            item.solutions = solutions.map((solution): Solution => {
              return {
                id: solution.id,
                user: new UserModel(solution.user, {}),
                contents: mapContents(solution.contents),
              };
            });
            item.comments = comments.map((comment) => new UnifiedCommentModel(comment, user));
          }
        }
      });

      if (promises) await Promise.all(promises);

      const objectiveExercises = [ExerciseType.OBJECTIVE, ExerciseType.OBJECTIVE_TRIVIA];
      const allAlternatives = lesson.items?.flatMap((item) => {
        if (item instanceof Exercise && objectiveExercises.includes(item.type))
          return item.alternatives;
        return [];
      });

      if (allAlternatives && allAlternatives.length > 0) {
        const assertions = await new UserGradesService().getAssertionsCorrections(
          (allAlternatives as Alternative[]).map((item) => item.id),
        );

        lesson.items?.forEach((item) => {
          if (item instanceof Exercise && objectiveExercises.includes(item.type)) {
            (item.alternatives as Alternative[]).forEach((exercise) => {
              const relatedAssertion = assertions.find(({ id }: any) => id === exercise.id);

              if (relatedAssertion) exercise.correct = relatedAssertion.correct;
            });
          }
        });
      }
    } catch (e) {
      SentryClient.captureError(e);
    }

    commit('SET_LESSON_CONTENTS', { lessonId: lesson.id, items: lesson.items });
  },
  async ADD_NEW_LESSON_REPLY({ commit }, payload: IAddLessonReplyPayload) {
    try {
      const { lessonId, exerciseId, dexExerciseId, userId, alternativeId, answer } = payload;
      const data: any = await new ExercisesEvaluationService().sendMultipleChoiceAnswer({
        assertionId: alternativeId,
        dexExerciseId,
        dexUserId: userId,
        answer,
      });

      const normalizedReply = normalizeReply(data);

      const solutions: Solution[] = normalizedReply.solutions.map((solution) => ({
        id: solution.id,
        contents: mapContents(solution.contents),
        user: new UserModel(solution.user, {}),
      }));

      commit('ADD_LESSON_REPLY', { lessonId, itemId: exerciseId, reply: normalizedReply.reply });
      commit('ADD_LESSON_SOLUTION', { lessonId, itemId: exerciseId, solutions });

      return { success: true };
    } catch (error) {
      ErrorHandler.showErrorMessage({
        customMessage: i18n.t('defaultErrorMessage').toString(),
        error,
      });

      return { success: false };
    }
  },
};

export default actions;
